import { checkPattern, PRESETS, presetOf, resolvePattern, matchesPattern, MAX_MESSAGE, MAX_REGEX } from './elementFormat/pattern';
import { ceilingFor, formatLimit, normalizeText } from './elementFormat/text';

/**
 * The web's words for a text element's rules (docs/plans/data-types-second-
 * pass.md §3.4, PR 2): an author's maxLength, a short text's case, trim and
 * pattern. The rules themselves are the vendored, byte-identical modules the
 * API writes with (utils/elementFormat/text.js, pattern.js); this file only
 * puts sentences to their tokens, for the element editor (TextOptions), the
 * item form's field and the API refusals that come back.
 */

// A pattern refused at authoring — the reason tokens of pattern.js
// checkPattern ('slow' is its cost bound, the same count the API makes, so
// the editor says it before saving). Mirrors keepr-api utils/typedElements.js
// PATTERN_REFUSALS in plain words.
export const PATTERN_REFUSALS = {
	shape: 'Write a pattern, or pick one from the list.',
	regex: 'Write a pattern, or pick one from the list.',
	preset: "keepr doesn't know that pattern. Pick one from the list.",
	both: 'Pick a preset or write a pattern, not both.',
	too_long: `A pattern can be at most ${MAX_REGEX} characters.`,
	flags: 'The only setting a pattern takes is letters in either case.',
	compile: "keepr can't read this pattern. Check its brackets and backslashes.",
	unsafe: 'This pattern repeats without limit something that itself repeats without limit, like (a+)+, and checking it could take minutes. Give the outer repeat a count, like {1,3}, or write the alternatives out with |.',
	slow: 'This pattern could take too long to check on a long value. Simplify it.',
	message: `Add the sentence a person reads when a value doesn't fit, up to ${MAX_MESSAGE} characters.`,
};

export const patternRefusalText = reason => PATTERN_REFUSALS[reason] || PATTERN_REFUSALS.shape;

// How a short text's letters are stored. '' is "as typed" (the option absent).
export const CASE_CHOICES = [
	{ value: '', label: 'As typed' },
	{ value: 'upper', label: 'Capitals' },
	{ value: 'lower', label: 'Small letters' },
];

export const CASE_HINTS = {
	'': 'Values are saved the way they are typed.',
	upper: 'Values are saved in capitals: "or-1234" is saved as OR-1234.',
	lower: 'Values are saved in small letters: "Ana@Example.com" is saved as ana@example.com.',
};

export { PRESETS, presetOf, checkPattern, resolvePattern, matchesPattern, MAX_MESSAGE, MAX_REGEX };

/** Which pattern control an element's stored pattern selects: 'none', a preset id, or 'custom'. */
export function patternChoiceOf(pattern) {
	if (!pattern || typeof pattern !== 'object') return 'none';
	if (presetOf(pattern.preset)) return pattern.preset;
	if (pattern.regex !== undefined && pattern.regex !== null) return 'custom';
	return 'none';
}

/** The sentence a value that does not fit an element's pattern is told, or null. */
export const patternMessageFor = element => resolvePattern(element?.options?.pattern)?.message || null;

/**
 * A value as it will be STORED: a short text's trim and case applied
 * (normalizeText, the API's own rule). Anything else is unchanged.
 */
export const storedTextValue = (value, element) => normalizeText(value, element?.dataType, element?.options || {});

/**
 * The maxLength a sub-editor may accept for this element: 1 to the ceiling
 * (the extended one under "Allow longer text"). null for a type without one.
 */
export const maxLengthCeiling = (dataType, options) => ceilingFor(dataType, options || {});

export const maxLengthRangeText = (dataType, options) => {
	const ceiling = maxLengthCeiling(dataType, options);
	return ceiling === null ? null : `Enter a whole number from 1 to ${formatLimit(ceiling)}.`;
};
